// -----------------------------------------------------------------------------
// UNDO / REDO on the issue page (A5) — v0.9.0, round 30 #5 (Kyle 30/Oct/26).
// Ctrl+Z (⌘Z) undoes the last change made on this issue during this visit; Ctrl+Y or Ctrl+Shift+Z (⌘⇧Z) redoes it.
// What can be undone: issue fields (status, discipline, texts, dates), Print on PDF, adding / editing / deleting a
// resolution log entry. Not undone (they ask first or are final): removing a snapshot, voiding the issue.
// Not while typing in a box (the box's own undo works there). A new change after an undo clears the redo list.
// Every step calls the same API as the original action, so the database rules and Activity apply as usual.
// Tests: tests/unit/undo.test.ts.
// -----------------------------------------------------------------------------
export interface Step { label: string; undo: () => Promise<void>; redo: () => Promise<void> }

export class UndoStack {
  private done: Step[] = [];
  private undone: Step[] = [];
  private busy = false;
  constructor(private max = 50) {}
  /** a change was just made */
  push(s: Step): void { this.done.push(s); if (this.done.length > this.max) this.done.shift(); this.undone = []; }
  get canUndo(): boolean { return this.done.length > 0; }
  get canRedo(): boolean { return this.undone.length > 0; }
  /** returns the label of what was undone, or null when there was nothing (or a step is still running) */
  async undo(): Promise<string | null> {
    if (this.busy || !this.done.length) return null;
    const s = this.done.pop()!;
    this.busy = true;
    try { await s.undo(); this.undone.push(s); return s.label; }
    catch (e) { this.done.push(s); throw e; }
    finally { this.busy = false; }
  }
  async redo(): Promise<string | null> {
    if (this.busy || !this.undone.length) return null;
    const s = this.undone.pop()!;
    this.busy = true;
    try { await s.redo(); this.done.push(s); return s.label; }
    catch (e) { this.undone.push(s); throw e; }
    finally { this.busy = false; }
  }
  clear(): void { this.done = []; this.undone = []; }
}

/** Which keyboard shortcut a keydown is: "undo", "redo" or null. */
export function undoKey(e: { key: string; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean; altKey: boolean }): "undo" | "redo" | null {
  if (!(e.ctrlKey || e.metaKey) || e.altKey) return null;
  const k = e.key.toLowerCase();
  if (k === "z") return e.shiftKey ? "redo" : "undo";
  if (k === "y" && !e.shiftKey) return "redo";
  return null;
}
